<script lang="ts" setup>
import { UNMARKED_TAB, useTask } from "@/stores/task";
import BaseImg from "@/components/mobile/common/BaseImg.vue";
import { Progress, showLoadingToast, showSuccessToast } from "vant";
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import ActionBtn from "./ActionBtn.vue";

import { getDisplayDateTime } from "@/modules/common";
import { watch } from "vue";
import i18n from "@/locales";

const BASE_MS = 15 * 1000;
const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

const taskStore = useTask();
const { t } = useI18n();
const task = ref<Task>();

watch(
  () => taskStore.taskMap.get(props.id),
  (newTask) => {
    task.value = newTask;
  },
  {
    deep: true,
  }
);

let _dtEstimate = 0;

const restMs = ref(0);
const completionPercent = computed(() => {
  const b = BASE_MS;
  const a = BASE_MS - restMs.value;
  return Math.min(Math.floor((a / b) * 100), 99);
});
const taskStatus = ref<"pending" | "processing" | "timeout">("pending");
const resetTimeAndStatus = () => {
  if (taskStatus.value === "timeout") return;
  restMs.value = _dtEstimate - Date.now();
  if (restMs.value <= 0) {
    taskStatus.value = "timeout";
  } else if (restMs.value <= BASE_MS) {
    taskStatus.value = "processing";
  } else {
    taskStatus.value = "pending";
  }
  setTimeout(resetTimeAndStatus, 1000);
};

const clickDel = async () => {
  const loadingToast = showLoadingToast(
    i18n.global.t("Message.cancellingMission")
  );
  try {
    await taskStore.deleteTask(props.id);
    loadingToast.close();
    showSuccessToast(i18n.global.t("Message.cancelSuccessfully"));
  } finally {
    loadingToast.close();
  }
};

const onRetryTask = async () => {
  const loadingToast = showLoadingToast(
    i18n.global.t("Message.checkingTheTask")
  );
  try {
    const isAlreadyFinished = await taskStore.retryTask(props.id);
    loadingToast.close();
    if (!isAlreadyFinished) {
      showSuccessToast({
        message: i18n.global.t("Message.taskCompletedPleaseCheckToMarked"),
        duration: 1500,
      });
    } else {
      showSuccessToast({
        message: i18n.global.t("Message.newTaskBeenCreated"),
        duration: 1500,
      });
    }
  } catch (e) {
    loadingToast.close();
  }
};

onMounted(() => {
  task.value = taskStore.getTask(props.id)!;
  const { dtEstimate } = taskStore.getTask(props.id)!;
  _dtEstimate = Number(new Date(dtEstimate!));
  resetTimeAndStatus();
});

const TaskIconMap: any = {
  MateTrait: "icon-emoji",
  StoryImage: "icon-story",
  StoryVideo: "icon-play",
  TextGif: "icon-gif",
  TextImage: "icon-paint",
};
</script>

<template>
  <!-- 等待中的任务 -->
  <div
    v-if="taskStore.selectedType === 0"
    class="mx-2.5 mt-1 pb-3 rounded-md shadow-primary shadow-md"
  >
    <div :class="['flex justify-between px-3 pt-3 text-[#ABABAB]']">
      <div class="flex items-center">
        <i :class="['iconfont', TaskIconMap[task?.tab!]]" />
        <span class="pl-1 pr-2 font-bold">{{
          t(`task.tab.${task?.tab}`)
        }}</span>
        <span class="text-xs mr-2">#{{ task?.id }}</span>
        <i class="iconfont icon-info text-xs"></i>
      </div>
      <div
        :class="['flex items-center font-bold', 'task-status-' + taskStatus]"
      >
        <i
          :class="[
            'iconfont mr-1',
            taskStatus === 'timeout' ? 'icon-overtime' : 'icon-waiting',
          ]"
        ></i>
        {{ t(`task.status.${taskStatus}`) }}
      </div>
    </div>
    <div v-if="taskStatus !== 'timeout'" class="flex flex-col px-3 mt-2">
      <div class="flex text-base-content/50">
        <div v-if="restMs > 0">
          {{ $t("Texts.estimatedRemainingTime") }}:
          {{ Math.floor(restMs / 1000) }} {{ $t("Config.seconds") }}
        </div>
      </div>
      <div
        v-if="taskStatus === 'processing'"
        class="flex justify-between items-center gap-4"
      >
        <progress
          class="progress progress-success"
          :value="completionPercent"
          :max="100"
        />
        <div>{{ completionPercent }}%</div>
      </div>
    </div>
    <div class="mt-3 pl-3 text-[#ABABAB]">
      {{ $t("Texts.createAt") }}：{{ getDisplayDateTime(task?.dtCreate!) }}
    </div>
    <div class="mt-3 flex justify-end px-3" v-if="taskStatus === 'timeout'">
      <ActionBtn
        :id="task?.id!"
        btn="rotate-right"
        class="mr-2"
        is-icon
        @on-click-btn="onRetryTask"
      />
      <ActionBtn
        :id="task?.id!"
        is-icon
        btn="delete"
        @on-click-btn="clickDel"
      />
    </div>
  </div>
  <!-- 其他任务 -->
  <div
    v-else
    :class="[
      taskStore.selectedType === 1 && '',
      [2, 3].includes(taskStore.selectedType) && 'flex flex-1 items-center',
    ]"
  >
    <video
      v-if="task?.entity.media?.url.endsWith('.mp4')"
      class="w-full mb-1"
      controls
      :src="task?.entity.media?.url"
      :poster="task?.entity.media?.videoCoverUrl"
      crossorigin="anonymous"
    />
    <BaseImg
      v-else
      :key="task?.entity.media?.url"
      classExtra="w-full mb-1"
      :media="task?.entity.media"
      :ratio="(task?.entity as TextImage)?.ratio"
      :width="taskStore.selectedType === UNMARKED_TAB ? 0 : 400"
      :entity="task?.entity"
    />

    <div
      v-if="task?.tab"
      :class="[
        'flex justify-between px-3 pb-2',
        `header-${taskStore.selectedType}`,
      ]"
    >
      <div class="flex items-center">
        <i :class="['iconfont', TaskIconMap[task?.tab]]" />
        <span class="pl-1 pr-2 font-normal">
          {{ t(`task.tab.${task?.tab}`) }}
        </span>
        <span class="text-xs">#{{ task?.id }}</span>
      </div>
      <div>
        <i class="iconfont icon-info text-lg"></i>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-0,
.header-2,
.header-3 {
  display: none;
}

.task-status-timeout {
  color: #ff1f1f;
}
.task-status-processing {
  color: #00da39;
}
.task-status-pending {
  color: #fed500;
}
</style>
